/**
 * 发票复印件附件 —— 上传、查看与手写内容订正。
 *
 * 【这个弹窗存在的意义】
 * 纸质票的复印件常带手写签字、日期与批注，那些内容原先只能留在纸上。
 * 这里把复印件留存下来，并把系统认出（或人工补录）的手写内容一并入库，
 * 供后续核对与审计。
 *
 * 【两条必须守住的显示规则】
 *  1. **通道未启用 ≠ 没有手写内容**。`UNAVAILABLE` 渲染成红色并原样展示原因，
 *     绝不显示成"未识别到手写内容"——前者是"没做"，后者是"做了没找到"，
 *     把两者混起来，用户会以为系统已经核对过票面。
 *  2. **修正必须留痕**。原因至少 8 个字，且提交后在留痕区能看到
 *     "改前 / 改后 / 谁 / 何时 / 为什么"，同时**原始识别结果始终可查**。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  HandwritingStatus,
  INVOICE_ACCEPT,
  MAX_UPLOAD_LABEL,
  PermissionCode,
  type HandwritingLine,
  type InvoiceAttachment,
} from '@ilc/shared';
import { invoiceApi } from '@/api/endpoints';
import { useAuthStore } from '@/stores/auth';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';
import { FileUploader } from '@/components/FileUploader';
import { HandwritingStatusTag } from '@/components/AttachmentStatusTag';

export interface InvoiceAttachmentsProps {
  /** 目标发票。null 表示弹窗关闭 */
  invoice: { id: string; bizNo: string } | null;
  onClose: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/* ==========================================================================
 * 手写内容订正
 * ========================================================================== */

function HandwritingEditModal({
  attachment,
  onClose,
  onSaved,
}: {
  attachment: InvoiceAttachment | null;
  onClose: () => void;
  onSaved: (next: InvoiceAttachment) => void;
}): React.ReactElement {
  const [form] = Form.useForm<{ text: string; reason: string }>();
  const [saving, setSaving] = useState(false);

  /**
   * 初值交给 Form 自己按 key 初始化，**不要**改成 openXxx 里调 setFieldsValue。
   *
   * 弹窗带 `destroyOnHidden`，关闭时会卸载内部的 `<Form>`；此刻 form 实例处于
   * "未连接"状态，同步调 `setFieldsValue` 会被直接丢弃，等 Form 重新挂载时
   * 又按 initialValues（空）初始化 —— 表现为"弹窗打开了、标题对、里面全空"。
   * 这是本项目已经踩过两次的坑，`LegalWorkbench.tsx` 与 `Roles.tsx` 都有记录。
   */
  const initialValues = useMemo(
    () => ({ text: attachment?.handwritingText ?? '', reason: '' }),
    [attachment],
  );

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    const reason = String(values.reason ?? '').trim();
    if (reason.length < 8) {
      feedback.warning('修正原因至少 8 个字，将写入留痕供审计追溯');
      return;
    }
    if (!attachment) return;

    setSaving(true);
    const text = values.text.trim() === '' ? null : values.text;
    const result = await performAction({
      action: () => invoiceApi.updateHandwriting(attachment.id, { text, reason }),
      successMessage: '手写内容已修正并留痕',
      offlineMessage:
        '后端修正接口尚未就绪，本次修改仅在本地演示展示，未写入数据库。',
      offline: () => {
        onSaved({ ...attachment, handwritingText: text, manuallyEdited: true });
      },
    });
    setSaving(false);
    if (result.ok && result.data) onSaved(result.data as InvoiceAttachment);
    if (result.ok) onClose();
  };

  return (
    <Modal
      open={attachment !== null}
      title="订正手写内容"
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={saving}
      okText="保存并留痕"
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="原始识别结果会一并保留"
        description="订正只改「当前生效内容」，机器当初认出的原文仍然可查——留痕要能回答「机器认的是什么、人改成了什么」。"
      />
      <Form
        key={attachment?.id ?? 'none'}
        form={form}
        layout="vertical"
        initialValues={initialValues}
      >
        <Form.Item
          name="text"
          label="手写内容"
          extra="以原件为准。留空表示这张复印件上其实没有手写内容。"
        >
          <Input.TextArea rows={6} placeholder="例如：签收人 王五  日期 2026-09-16" />
        </Form.Item>
        <Form.Item
          name="reason"
          label="修正原因"
          rules={[{ required: true, message: '必须填写修正原因' }]}
          extra="至少 8 个字。这份内容会被当作「票面上写的原话」用于后续核对，没有原因就无法追溯。"
        >
          <Input.TextArea rows={2} placeholder="例如：原件上确为手写签收，机器未识别出内容，按原件补录" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ==========================================================================
 * 附件详情
 * ========================================================================== */

function HandwritingLines({ lines }: { lines: HandwritingLine[] }): React.ReactElement {
  if (lines.length === 0) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        通道未返回逐行明细（不返回坐标或逐行置信度时属正常）
      </Typography.Text>
    );
  }
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {lines.map((line, i) => (
        <div key={`${line.text}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span className="ilc-mono" style={{ fontSize: 12, minWidth: 20, color: 'rgba(0,0,0,0.45)' }}>
            {i + 1}
          </span>
          <span style={{ flex: 1, wordBreak: 'break-all' }}>{line.text}</span>
          {line.confidence !== null && line.confidence < 80 ? (
            <Tooltip title="置信度偏低，建议对照原件核对">
              <Tag color="orange">置信度 {line.confidence}</Tag>
            </Tooltip>
          ) : null}
        </div>
      ))}
    </Space>
  );
}

function AttachmentDetailDrawer({
  attachment,
  onClose,
  onChanged,
}: {
  attachment: InvoiceAttachment | null;
  onClose: () => void;
  onChanged: () => void;
}): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [editing, setEditing] = useState<InvoiceAttachment | null>(null);
  const [busy, setBusy] = useState(false);

  const openOriginal = async (id: string): Promise<void> => {
    await performAction({
      action: async () => {
        const { url } = await invoiceApi.attachmentUrl(id);
        window.open(url, '_blank', 'noopener');
        return url;
      },
      successMessage: '',
    });
  };

  const reparse = async (id: string): Promise<void> => {
    setBusy(true);
    const r = await performAction({
      action: () => invoiceApi.reparseAttachment(id),
      successMessage: '已重新提取',
    });
    setBusy(false);
    if (r.ok) onChanged();
  };

  const history = attachment?.editHistory ?? [];

  return (
    <>
      <Drawer
        open={attachment !== null}
        onClose={onClose}
        width={720}
        title="复印件详情"
        extra={
          attachment ? (
            <Space>
              <Button icon={<EyeOutlined />} onClick={() => void openOriginal(attachment.id)}>
                查看原件
              </Button>
              <Button
                icon={<ReloadOutlined />}
                loading={busy}
                disabled={!can(PermissionCode.INVOICE_ATTACHMENT_WRITE)}
                onClick={() => void reparse(attachment.id)}
              >
                重新提取
              </Button>
            </Space>
          ) : null
        }
      >
        {attachment ? (
          <>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="文件名">{attachment.fileName}</Descriptions.Item>
              <Descriptions.Item label="大小">{formatSize(attachment.fileSize)}</Descriptions.Item>
              <Descriptions.Item label="实际类型">
                <Tag>{attachment.fileKind}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="上传时间">
                {attachment.createdAt.replace('T', ' ').slice(0, 19)}
              </Descriptions.Item>
              <Descriptions.Item label="手写提取" span={2}>
                <HandwritingStatusTag
                  status={attachment.handwritingStatus}
                  impl={attachment.handwritingImpl}
                  mode={attachment.handwritingMode}
                  message={attachment.handwritingMessage}
                  missing={attachment.handwritingMissing}
                />
              </Descriptions.Item>
            </Descriptions>

            {attachment.handwritingMessage ? (
              <Alert
                type={
                  attachment.handwritingStatus === HandwritingStatus.UNAVAILABLE ||
                  attachment.handwritingStatus === HandwritingStatus.FAILED ||
                  attachment.handwritingStatus === HandwritingStatus.TIMEOUT
                    ? 'warning'
                    : 'info'
                }
                showIcon
                style={{ marginTop: 12 }}
                message={attachment.handwritingMessage}
                description={
                  attachment.handwritingMissing?.length ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      需补齐：{attachment.handwritingMissing.join('、')}
                    </Typography.Text>
                  ) : null
                }
              />
            ) : null}

            <Divider orientation="left" style={{ marginTop: 20 }}>
              手写内容
            </Divider>

            {attachment.manuallyEdited ? (
              <Tag color="blue" style={{ marginBottom: 8 }}>
                已人工订正
              </Tag>
            ) : null}

            {attachment.handwritingText ? (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  padding: 12,
                  wordBreak: 'break-all',
                }}
              >
                {attachment.handwritingText}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  attachment.handwritingStatus === HandwritingStatus.UNAVAILABLE
                    ? '尚未做手写识别（通道未启用），下方可人工补录'
                    : '未识别到手写内容'
                }
              />
            )}

            <Space style={{ marginTop: 12 }}>
              <Button
                icon={<EditOutlined />}
                disabled={!can(PermissionCode.INVOICE_ATTACHMENT_WRITE)}
                onClick={() => setEditing(attachment)}
              >
                订正内容
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                订正需填写原因并留痕；原始识别结果不会被覆盖。
              </Typography.Text>
            </Space>

            {attachment.handwritingOriginal &&
            attachment.handwritingOriginal !== attachment.handwritingText ? (
              <Alert
                type="info"
                style={{ marginTop: 12 }}
                message="机器当初识别出的原文（与当前生效内容不同）"
                description={
                  <span style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                    {attachment.handwritingOriginal}
                  </span>
                }
              />
            ) : null}

            {(attachment.handwritingLines?.length ?? 0) > 0 ? (
              <>
                <Divider orientation="left">逐行明细</Divider>
                <HandwritingLines lines={attachment.handwritingLines ?? []} />
              </>
            ) : null}

            <Divider orientation="left">订正留痕</Divider>
            {history.length === 0 ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                无人工订正记录
              </Typography.Text>
            ) : (
              <Timeline
                items={history.map((h) => ({
                  children: (
                    <div>
                      <Space size={6} wrap>
                        <Tag color="blue">{h.operatorName}</Tag>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {h.at.replace('T', ' ').slice(0, 19)}
                        </Typography.Text>
                      </Space>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <div>
                          <span style={{ color: 'rgba(0,0,0,0.45)' }}>改前：</span>
                          {h.before ?? <Typography.Text type="secondary">（空）</Typography.Text>}
                        </div>
                        <div>
                          <span style={{ color: 'rgba(0,0,0,0.45)' }}>改后：</span>
                          {h.after ?? <Typography.Text type="secondary">（空）</Typography.Text>}
                        </div>
                        <div>
                          <span style={{ color: 'rgba(0,0,0,0.45)' }}>原因：</span>
                          {h.reason}
                        </div>
                      </div>
                    </div>
                  ),
                }))}
              />
            )}
          </>
        ) : null}
      </Drawer>

      <HandwritingEditModal
        attachment={editing}
        onClose={() => setEditing(null)}
        onSaved={onChanged}
      />
    </>
  );
}

/* ==========================================================================
 * 主弹窗
 * ========================================================================== */

export function InvoiceAttachments({
  invoice,
  onClose,
}: InvoiceAttachmentsProps): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [items, setItems] = useState<InvoiceAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InvoiceAttachment | null>(null);

  const canWrite = can(PermissionCode.INVOICE_ATTACHMENT_WRITE);

  const load = useCallback(async (): Promise<void> => {
    if (!invoice) return;
    setLoading(true);
    const result = await performAction({
      action: () => invoiceApi.listAttachments(invoice.id),
      successMessage: '',
      offline: () => setItems([]),
    });
    if (result.ok && Array.isArray(result.data)) setItems(result.data as InvoiceAttachment[]);
    setLoading(false);
  }, [invoice]);

  useEffect(() => {
    if (invoice) void load();
    else setItems([]);
  }, [invoice, load]);

  /** 打开详情要**单独取一次**——列表行不带手写全文与逐行明细 */
  const openDetail = async (id: string): Promise<void> => {
    const result = await performAction({
      action: () => invoiceApi.attachmentDetail(id),
      successMessage: '',
    });
    if (result.ok && result.data) setDetail(result.data as InvoiceAttachment);
  };

  const remove = async (id: string): Promise<void> => {
    const result = await performAction({
      action: () => invoiceApi.removeAttachment(id),
      successMessage: '附件已删除',
    });
    if (result.ok) {
      setDetail(null);
      void load();
    }
  };

  const hasUnavailable = useMemo(
    () => items.some((a) => a.handwritingStatus === HandwritingStatus.UNAVAILABLE),
    [items],
  );

  return (
    <>
      <Modal
        open={invoice !== null}
        title={`发票复印件${invoice ? ` · ${invoice.bizNo}` : ''}`}
        onCancel={onClose}
        footer={null}
        width={860}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          上传纸质发票的复印件（拍照或扫描件），系统会提取票面上的手写内容（签字、日期、批注）并入库留档。
        </Typography.Paragraph>

        {canWrite ? (
          <FileUploader
            accept={INVOICE_ACCEPT}
            upload={(file, onProgress) =>
              invoiceApi.uploadAttachment(invoice!.id, file, onProgress)
            }
            onUploaded={() => void load()}
            title="点击或拖拽上传发票复印件"
            description={
              <>
                支持 PDF 与图片，单个不超过 {MAX_UPLOAD_LABEL}。
                图片会提取手写内容；PDF 只存档不提取（接口不支持逐页，可另传一张照片）。
              </>
            }
            successMessage="复印件已上传"
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="您没有「管理发票附件」权限，只能查看已有复印件。"
          />
        )}

        {hasUnavailable ? (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: 12 }}
            message="有附件未做手写识别"
            /* 这里是 alert 的纯文本 description，不渲染 markdown——
               从前写成 `**没有做**`，用户看到的就是两个星号 */
            description="下方的「通道未激活」表示这次【没有做】手写识别，不等于票面上没有手写内容。原件仍可下载人工核对。"
          />
        ) : null}

        <Table
          size="small"
          rowKey="id"
          style={{ marginTop: 12 }}
          loading={loading}
          dataSource={items}
          pagination={false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚无复印件" /> }}
          columns={[
            {
              title: '文件名',
              dataIndex: 'fileName',
              render: (v: string, row) => (
                <Space size={4}>
                  <FileSearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                  <span style={{ wordBreak: 'break-all' }}>{v}</span>
                  {row.manuallyEdited ? <Tag color="blue">已订正</Tag> : null}
                </Space>
              ),
            },
            {
              title: '大小',
              dataIndex: 'fileSize',
              width: 90,
              render: (v: number) => <span className="ilc-mono" style={{ fontSize: 12 }}>{formatSize(v)}</span>,
            },
            {
              title: '手写内容',
              key: 'handwriting',
              width: 150,
              /**
               * 列表里**不显示 impl**——`unavailable(手写识别通道未就绪)` 这种
               * 实现名在 150px 的列宽里必然被截断，反而看不清结论。
               * 实现名属于诊断细节，详情抽屉里完整给出。
               */
              render: (_v, row) => (
                <HandwritingStatusTag
                  status={row.handwritingStatus}
                  mode={row.handwritingMode}
                  message={row.handwritingMessage}
                  missing={row.handwritingMissing}
                />
              ),
            },
            {
              title: '操作',
              key: 'actions',
              width: 170,
              render: (_v, row) => (
                <Space size={4}>
                  <Button type="link" size="small" onClick={() => void openDetail(row.id)}>
                    详情
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    disabled={!canWrite}
                    onClick={() => void (async () => {
                      const r = await performAction({
                        action: () => invoiceApi.attachmentUrl(row.id),
                        successMessage: '',
                      });
                      if (r.ok && r.data) {
                        window.open((r.data as { url: string }).url, '_blank', 'noopener');
                      }
                    })()}
                  >
                    查看原件
                  </Button>
                  {canWrite ? (
                    <Popconfirm
                      title="删除这份复印件？"
                      description="原件文件会保留待回收，记录将不再显示。"
                      onConfirm={() => void remove(row.id)}
                    >
                      <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      <AttachmentDetailDrawer
        attachment={detail}
        onClose={() => setDetail(null)}
        onChanged={() => {
          void load();
          if (detail) void openDetail(detail.id);
        }}
      />
    </>
  );
}

export default InvoiceAttachments;
